import ReactDOM from "react-dom/client";
import "./index.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import * as Sentry from "@sentry/react";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { supabaseClient } from "./lib/supabase";
import { routeTree } from "./routeTree.gen";

// Create a global error handler
window.addEventListener("error", (event) => {
  const isChunkError = event.message.includes("Failed to fetch dynamically imported module");

  if (isChunkError) {
    // Prompt user to reload
    if (confirm("A new version is available. Reload to get the latest version?")) {
      window.location.reload();
    }
  }
});

// For unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  const reasonMessage = event.reason?.message;

  // Check for the known module loading failures
  const isChunkError =
    reasonMessage?.includes("Failed to fetch dynamically imported module") ||
    reasonMessage?.includes("Module load failed") || // Common generic message
    reasonMessage?.includes("Not a valid JavaScript MIME type"); // Could catch related errors

  if (isChunkError) {
    event.preventDefault();
    Sentry.logger.warn("Stale application version detected. Prompting for reload.");

    if (
      confirm("A new version of the application is available. Reload to get the latest update?")
    ) {
      window.location.reload();
    }
  }
});

window.addEventListener("vite:preloadError", (event) => {
  window.location.reload(); // if js bundle not longer exist, since a new build is in cdn
});

// Create a new router instance
const router = createRouter({ routeTree, scrollRestoration: true });

Sentry.init({
  dsn: "https://12014523415cfdd25db05bdd36346263@o4509884657238016.ingest.us.sentry.io/4509884658548736",
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      networkDetailAllowUrls: [window.location.origin],
    }),
    supabaseIntegration(supabaseClient, Sentry, {
      tracing: true,
      breadcrumbs: true,
      errors: true,
    }),
    // Sentry.feedbackIntegration({
    //   colorScheme: "system",
    // }),
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/.+\.supabase\.co/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
  beforeSend: (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
    // Check if the application is running on localhost
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      console.log("Sentry event ignored on localhost:", event);
      return null;
    }

    return event;
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<RouterProvider router={router} />);
}
